import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import {
  canAnimate,
  CONTENT_ENTER_FROM,
  CONTENT_EXIT_MS,
  prefersReducedMotion,
  readChatSearchMotionTokens,
  tChatSearchMotionTokens,
} from './ChatSearch.motion';
import { tChatSearchContext } from './ChatSearch.types';

/**
 * How the bar entered, on its root as `data-motion`:
 *   grow    — from the magnifier, with a pointer (DES-TBD, see ChatSearch.motion);
 *   fade    — from the magnifier under prefers-reduced-motion: opacity only;
 *   instant — from the keyboard (Ctrl/⌘+F), or with no DS tokens to move by.
 */
export type tChatSearchBarEnterMode = 'grow' | 'fade' | 'instant';

export type tChatSearchBarMotionRefs = {
  root: RefObject<HTMLDivElement>;
  field: RefObject<HTMLDivElement>;
  /** The magnifier inside the field, which travels from the button. */
  icon: RefObject<HTMLSpanElement>;
  /** The field's background, built as a left cap, a scaled body and a right
   *  cap so that scaling it never squeezes the rounded corners. */
  growStart: RefObject<HTMLSpanElement>;
  growBody: RefObject<HTMLSpanElement>;
  growEnd: RefObject<HTMLSpanElement>;
  trailing: RefObject<HTMLDivElement>;
  close: RefObject<HTMLButtonElement>;
};

type tChatSearchBarMotionOptions = {
  search: tChatSearchContext | null;
  /** The search closed with "Cerrar" and the bar is playing its way out. */
  leaving: boolean;
  /** Called once the way out has played; the bar is unmounted after it. */
  onLeft: () => void;
  refs: tChatSearchBarMotionRefs;
};

/** `data-phase` on the root while the background layer stands in for the
 *  DS input's own border, background and focus ring. */
type tPhase = 'growing' | 'landed' | 'shrinking';

type tFlip = {
  start: string;
  body: string;
  end: string;
  icon: string;
};

const AT_REST = 'translate(0px, 0px)';

function translate(x: number, y: number): string {
  return `translate(${x}px, ${y}px)`;
}

function centerOf(rect: DOMRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

/**
 * FLIP: where each piece of the field has to start for the field to look like
 * the magnifier button, measured from openButtonRef against the field.
 */
function measureFlip(
  button: HTMLElement,
  refs: tChatSearchBarMotionRefs,
): tFlip | null {
  const field = refs.field.current;
  const cap = refs.growStart.current;
  const icon = refs.icon.current;

  if (!field || !cap || !icon) return null;

  const from = button.getBoundingClientRect();
  const to = field.getBoundingClientRect();
  const radius = cap.offsetWidth;
  const dy = centerOf(from).y - centerOf(to).y;
  // The body spans the field minus both caps, and scales from its left edge.
  const scale =
    Math.max(from.width - 2 * radius, 0) / Math.max(to.width - 2 * radius, 1);
  const iconTo = centerOf(icon.getBoundingClientRect());

  return {
    start: translate(from.left - to.left, dy),
    body: `${translate(from.left - to.left, dy)} scaleX(${scale})`,
    end: translate(from.right - to.right, dy),
    icon: translate(centerOf(from).x - iconTo.x, centerOf(from).y - iconTo.y),
  };
}

function chooseEnterMode(
  search: tChatSearchContext | null,
): tChatSearchBarEnterMode {
  if (search?.motion !== 'animate') return 'instant';
  if (!readChatSearchMotionTokens()) return 'instant';
  if (!canAnimate(document.documentElement)) return 'instant';
  if (prefersReducedMotion()) return 'fade';

  return search.openButtonRef.current ? 'grow' : 'fade';
}

function setPhase(root: HTMLElement, phase: tPhase | null) {
  if (phase) root.dataset.phase = phase;
  else delete root.dataset.phase;
}

function contentOf(
  search: tChatSearchContext | null,
  refs: tChatSearchBarMotionRefs,
): HTMLElement[] {
  const candidates: (HTMLElement | null | undefined)[] = [
    search?.inputRef.current,
    refs.trailing.current,
    refs.close.current,
  ];

  return candidates.filter((element): element is HTMLElement =>
    Boolean(element),
  );
}

type tPlay = {
  refs: tChatSearchBarMotionRefs;
  flip: tFlip;
  tokens: tChatSearchMotionTokens;
};

/** The grow, 0–300 ms; the content enters 240–300 ms. Returns the body's. */
function playGrow(play: tPlay, content: HTMLElement[]): Animation[] {
  const { refs, flip, tokens } = play;
  const timing = tokens.grow;
  const move = (element: HTMLElement | null, from: string, to: string) =>
    element?.animate([{ transform: from }, { transform: to }], timing);

  const pieces = [
    move(refs.growBody.current, flip.body, `${AT_REST} scaleX(1)`),
    move(refs.growStart.current, flip.start, AT_REST),
    move(refs.growEnd.current, flip.end, AT_REST),
    move(refs.icon.current, flip.icon, AT_REST),
  ];
  const delay = timing.duration * CONTENT_ENTER_FROM;
  const entering = content.map((element) =>
    element.animate(
      [
        { opacity: 0, transform: `translateX(${tokens.contentShift})` },
        { opacity: 1, transform: 'translateX(0px)' },
      ],
      {
        delay,
        duration: timing.duration - delay,
        easing: tokens.easeEnter,
        fill: 'backwards',
      },
    ),
  );

  return [...pieces, ...entering].filter((animation): animation is Animation =>
    Boolean(animation),
  );
}

/** "Cerrar": the content out in 40 ms, then the field back into the
 *  magnifier in 200 ms. Returns the body's first. */
function playShrink(play: tPlay, content: HTMLElement[]): Animation[] {
  const { refs, flip, tokens } = play;
  const timing: KeyframeAnimationOptions = {
    ...tokens.shrink,
    delay: CONTENT_EXIT_MS,
    fill: 'forwards',
  };
  const move = (element: HTMLElement | null, from: string, to: string) =>
    element?.animate([{ transform: from }, { transform: to }], timing);

  const pieces = [
    move(refs.growBody.current, `${AT_REST} scaleX(1)`, flip.body),
    move(refs.growStart.current, AT_REST, flip.start),
    move(refs.growEnd.current, AT_REST, flip.end),
    move(refs.icon.current, AT_REST, flip.icon),
  ];
  const leaving = content.map((element) =>
    element.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: CONTENT_EXIT_MS,
      easing: tokens.shrink.easing,
      fill: 'forwards',
    }),
  );

  return [...pieces, ...leaving].filter((animation): animation is Animation =>
    Boolean(animation),
  );
}

/**
 * The bar's enter and exit (SRE-3328; DES-TBD, see ChatSearch.motion).
 *
 * Only transform and opacity move, never width, left or clip-path. While the
 * background layer moves, the DS input's own border, background and focus
 * ring are off (`data-phase`); they come back at full width on one frame, so
 * the focus ring is never animated. The field is focused from the first frame
 * (autoFocus), so typing works throughout.
 *
 * The keyboard never animates: Ctrl/⌘+F mounts the bar as it is, and Esc
 * unmounts it (the caller never sets `leaving` for it). Results and marks are
 * not animated at all.
 *
 * @returns How the bar entered, for `data-motion`.
 */
export default function useChatSearchBarMotion(
  options: tChatSearchBarMotionOptions,
): tChatSearchBarEnterMode {
  const { search, leaving, onLeft, refs } = options;
  const [enterMode] = useState(() => chooseEnterMode(search));
  const searchRef = useRef(search);
  const onLeftRef = useRef(onLeft);
  const wasLeavingRef = useRef(false);

  searchRef.current = search;
  onLeftRef.current = onLeft;

  // Enter, once.
  useLayoutEffect(() => {
    const root = refs.root.current;
    const tokens = readChatSearchMotionTokens();
    const button = searchRef.current?.openButtonRef.current;

    if (!root || !tokens || enterMode === 'instant') return undefined;

    let running: Animation[] = [];
    let cancelled = false;

    if (enterMode === 'fade') {
      running = [root.animate([{ opacity: 0 }, { opacity: 1 }], tokens.grow)];
    } else {
      const flip = button ? measureFlip(button, refs) : null;

      if (!flip) return undefined;

      setPhase(root, 'growing');
      running = playGrow(
        { refs, flip, tokens },
        contentOf(searchRef.current, refs),
      );
      running[0]?.finished
        .then(() => {
          if (cancelled) return;

          // Full width: the DS input's border and focus ring come back on
          // this frame, still with its own transitions off, and those come
          // back on the next one, so the ring never fades in.
          setPhase(root, 'landed');
          requestAnimationFrame(() => {
            if (root.dataset.phase === 'landed') setPhase(root, null);
          });
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
      running.forEach((animation) => animation.cancel());
      if (root.dataset.phase === 'growing') setPhase(root, null);
    };
  }, []);

  // Exit with "Cerrar".
  useLayoutEffect(() => {
    const root = refs.root.current;

    if (!leaving) {
      // Opened again while it was leaving: the field is the one that was
      // there, so autoFocus will not run again.
      if (wasLeavingRef.current) searchRef.current?.inputRef.current?.focus();
      wasLeavingRef.current = false;

      return undefined;
    }

    wasLeavingRef.current = true;

    const tokens = readChatSearchMotionTokens();
    const button = searchRef.current?.openButtonRef.current;
    const left = () => onLeftRef.current();

    if (!root || !tokens || !canAnimate(root)) {
      left();

      return undefined;
    }

    root.getAnimations?.({ subtree: true }).forEach((animation) => {
      animation.cancel();
    });

    let running: Animation[];
    let cancelled = false;
    const flip =
      button && !prefersReducedMotion() ? measureFlip(button, refs) : null;

    if (flip) {
      setPhase(root, 'shrinking');
      running = playShrink(
        { refs, flip, tokens },
        contentOf(searchRef.current, refs),
      );
    } else {
      running = [
        root.animate([{ opacity: 1 }, { opacity: 0 }], {
          ...tokens.shrink,
          fill: 'forwards',
        }),
      ];
    }

    running[0]?.finished
      .then(() => {
        if (!cancelled) left();
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      running.forEach((animation) => animation.cancel());
      setPhase(root, null);
    };
  }, [leaving]);

  return enterMode;
}
